import React, { useEffect, useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
  Tooltip,
  Popover,
} from "@mui/material";
import GuruEditor from "ckeditor5-custom-build";
import CustomIconButton from "../../../../../../components/buttons/icons-buttons/CustomIconButton";
import SaveIconButton from "../../../../../../components/buttons/icons-buttons/SaveIconButton";
import {
  normalEditorConfig,
  getIntlTranslation,
  getBorderColor,
  getBackground,
} from "../../../../../../utils/helper";
import TextEditorHistory from "./TextEditorHistory";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import { resetState } from "./textEditorSlice";
import { useIntl } from "react-intl";

const RenderTextEditor = ({ node, onSave, inReviewMode = false, onSaveSuccess, onClose }) => {
  const intl = useIntl();
  const [currNode, setCurrNode] = useState(null);
  const [isTextUpdated, setIsTextUpdated] = useState(false);
  const [placeholderAnchorEl, setPlaceholderAnchorEl] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const state = useSelector((state) => state.textEditor);
  const [viewHistory, setViewHistory] = React.useState({
    open: false,
    label: null,
    data: null,
  });
  const editorRef = useRef();
  const dataRef = useRef(node);

  const handleSave = () => {
    if (!!editorRef.current) {
      const data = {
        type: "reportTextDto",
        inputValue: editorRef.current.getData(),
        reportIdent: dataRef.current.ident,
        syncData: false,
      };
      onSave(data);
    }
  };

  const handlePlaceholderMouseEnter = (event) => {
    setPlaceholderAnchorEl(event.currentTarget);
  };

  const handlePlaceholderMouseLeave = () => {
    setPlaceholderAnchorEl(null);
  };

  const copyPlaceholderText = () => {
    const placeholderText = getPlaceholderText(currNode.label);
    navigator.clipboard.writeText(placeholderText).then(() => {
      enqueueSnackbar(intl.formatMessage({ id: "_placeholder-text-copied" }) || "Placeholder text copied to clipboard", {
        variant: "success",
        autoHideDuration: 2000,
      });
    }).catch(() => {
      enqueueSnackbar(intl.formatMessage({ id: "_copy-failed" }) || "Failed to copy text", {
        variant: "error",
        autoHideDuration: 2000,
      });
    });
  };

  const placeholderPopoverOpen = Boolean(placeholderAnchorEl);

  useEffect(() => {
    if (!!node) {
      dataRef.current = node;
      setCurrNode(node);
    }
  }, [node]);

  useEffect(() => {
    if (
      state.saved.type === "SAVED" &&
      !!state.saved.data &&
      dataRef.current?.ident === state.saved.data.reportIdent
    ) {
      state.saved.messages.forEach((element) => {
        enqueueSnackbar(element, {
          variant: "success",
          autoHideDuration: 2000,
        });
      });

      const savedData = {
        ...dataRef.current,
        ident: state.saved.data.reportIdent,
        value: state.saved.data.inputValue,
      };
      setCurrNode(savedData);
      dataRef.current = savedData;
      dispatch(resetState());
      setIsTextUpdated(false);
      onSaveSuccess(savedData);
    }
  }, [dispatch, enqueueSnackbar, state.saved]);

  // Get placeholder text using translations - works with both project and system language
  const getPlaceholderText = (label) => {
    if (!label) return null;
    
    const normalizedLabel = label.toUpperCase().trim();
    
    // Mapping of label patterns to placeholder keys (supports both EN and DE)
    const labelPatterns = [
      {
        patterns: ['BUILDING CONSTRUCTION', 'BAUKONSTRUKTION', 'BAUKONSTR'],
        key: '_placeholder-building-construction'
      },
      {
        patterns: ['TECHNICAL BUILDING EQUIPMENT', 'TGA', 'TECHNISCHE GEBÄUDEAUSRÜSTUNG'],
        key: '_placeholder-technical-building-equipment'
      },
      {
        patterns: ['OUTDOOR FACILITIES', 'AUSSENANLAGEN', 'AUßENANLAGEN'],
        key: '_placeholder-outdoor-facilities'
      },
      {
        patterns: ['FIRE PROTECTION', 'FIRE, LIFE & SAFETY', 'BRANDSCHUTZ'],
        key: '_placeholder-fire-protection'
      },
      {
        patterns: ['CONFORMITY', 'KONFORMITÄT', 'KONFORMITAT'],
        key: '_placeholder-conformity'
      }
    ];
    
    // Find matching pattern
    for (const { patterns, key } of labelPatterns) {
      if (patterns.some(pattern => normalizedLabel.includes(pattern))) {
        return intl.formatMessage({ id: key }) || getIntlTranslation(key);
      }
    }
    
    return null;
  };

  // Check if current label has placeholder text defined
  const hasPlaceholderText = (label) => {
    return getPlaceholderText(label) !== null;
  };

  return (
    <>
      {!!currNode ? (
        <>
          <Box
            className="d-flex align-items-baseline w-100 flex-wrap p-1"
            key={currNode.ident}
            style={{ 
              marginTop: 10,
              borderLeft: getBorderColor(isTextUpdated),
              backgroundColor: getBackground(isTextUpdated)
            }}
          >
            {(!!currNode.label && !inReviewMode) && (
              <Box style={{ width: "100%" }} className="d-flex align-items-center justify-content-between">
                <Box className="d-flex align-items-center">
                  <Typography
                    style={{
                      fontSize: 11,
                      color: "#254a9a",
                      fontWeight: "bold",
                    }}
                    className="m-1"
                  >
                    {currNode.label}
                  </Typography>
                  {currNode.userDefined && hasPlaceholderText(currNode.label) && (
                    <IconButton
                      size="small"
                      onMouseEnter={handlePlaceholderMouseEnter}
                      onMouseLeave={handlePlaceholderMouseLeave}
                      style={{
                        color: "#254a9a",
                        padding: 2,
                        marginLeft: 4
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            )}
            <Box
              className="d-flex align-items-baseline"
              style={{
                minHeight: "200px",
                width: "100%",
              }}
            >
              {!!currNode && currNode.userDefined ? (
                <>
                  <Box
                    id={`editor_box_${currNode.ident}`}
                    className="g-text-editor custom-scrollBar p-1"
                    style={{
                      minHeight: "200px",
                      width: "100%",
                      height: "98%",
                    }}
                  >
                    <CKEditor
                      id={`editor_${currNode.ident}`}
                      ref={editorRef}
                      editor={GuruEditor.ClassicEditor}
                      onReady={(editor) => {
                        editorRef.current = editor;
                      }}
                      onChange={(event, editor) => setIsTextUpdated(dataRef.current.text !== editor.getData())}
                      disabled={!currNode.userDefined}
                      config={{
                        ...normalEditorConfig,
                        placeholder: intl.formatMessage({ id:"_text-editor-placeholder"}),
                        // Remove placeholder from editor config since we're showing it in popover
                        enterMode: 1, // CKEDITOR.ENTER_P
                        shiftEnterMode: 2, // CKEDITOR.ENTER_BR
                      }}
                      data={currNode.text}
                    />
                  </Box>
                  <Box
                    style={{
                      width: "10%",
                      minHeight: "200px",
                      position: "sticky",
                      top: "0",
                    }}
                    className="d-flex flex-column align-items-baseline"
                  >
                    <>
                    {inReviewMode && 
                    <IconButton
                      edge="start"
                      id={`close_${node.ident}`}
                      onClick={(e) => onClose(node)}
                      className="w-100 p-2"
                      aria-label="close"
                    >
                      <CloseIcon style={{ color: "#d08f8e" }} />
                    </IconButton>
                    }

                      <SaveIconButton
                        id={`save_${currNode.ident}`}
                        edge="start"
                        style={{ marginLeft: 0, width: "100%" }}
                        handleClick={() => {
                          handleSave();
                        }}
                        tooltip={{
                          title: getIntlTranslation("tooltip.Save-report-Text"),
                        }}
                        color={"#254a9a"}
                        label={"_save"}
                      />
                      {!inReviewMode && 
                      <CustomIconButton
                        id={`show_history_${currNode.ident}`}
                        icon={<HistoryIcon />}
                        edge="start"
                        style={{ marginLeft: 0, width: "100%" }}
                        handleClick={(e) => {
                          setViewHistory({
                            open: true,
                            label: null,
                            data: null,
                          });
                        }}
                        color="primary"
                        tooltip={{
                          title: getIntlTranslation("_history"),
                        }}
                        label={"_history"}
                        aria-label="show history"
                      />
                        }
                    </>
                  </Box>
                </>
              ) : (
                <Typography
                  style={{
                    fontSize: 11,
                    width: "90%",
                    textAlign: "justify",
                  }}
                  dangerouslySetInnerHTML={{ __html: currNode.text }}
                  className="label-text"
                ></Typography>
              )}
            </Box>
          </Box>

          {/* Placeholder Text Popover - Only show if has placeholder text */}
          {currNode && hasPlaceholderText(currNode.label) && (
            <Popover
              open={placeholderPopoverOpen}
              anchorEl={placeholderAnchorEl}
              onClose={() => setPlaceholderAnchorEl(null)}
              disableRestoreFocus
              onMouseEnter={() => setPlaceholderAnchorEl(placeholderAnchorEl)}
              onMouseLeave={handlePlaceholderMouseLeave}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                style: {
                  maxWidth: 500,
                  maxHeight: 400,
                  padding: 16,
                  pointerEvents: 'auto',
                },
                onMouseEnter: () => setPlaceholderAnchorEl(placeholderAnchorEl),
                onMouseLeave: handlePlaceholderMouseLeave,
              }}
            >
              <Box>
                <Box className="mb-2">
                  <Typography variant="subtitle2" style={{ color: "#254a9a", fontWeight: "bold" }}>
                    {currNode.label} - {intl.formatMessage({ id: "_sample-text" }) || "Sample Text"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: 12,
                    lineHeight: 1.4,
                    textAlign: "justify",
                    whiteSpace: "pre-line",
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {getPlaceholderText(currNode.label)}
                </Typography>
              </Box>
            </Popover>
          )}

          {viewHistory.open && (
            <TextEditorHistory
              isOpen={viewHistory.open}
              rtIdent={currNode.ident}
              currentValue={currNode.text}
              close={() =>
                setViewHistory({
                  open: false,
                  label: null,
                  data: null,
                })
              }
            />
          )}
        </>
      ) : (
        <List>
          {[1, 2, 3, 4, 5].map((index) => (
            <ListItem key={index}>
              <ListItemText>
                <Skeleton variant="text" />
              </ListItemText>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
};

export default RenderTextEditor;
